import React, { useRef, useState } from 'react';
import { Upload, File, X } from 'lucide-react';
import '../../styles/design-system.css';

/**
 * FileUploadZone Component
 * Drag-and-drop file upload area matching iLovePDF design
 * 
 * @param {Object} props
 * @param {Function} props.onFilesSelected - Callback when files are selected
 * @param {string[]} props.acceptedTypes - Array of accepted MIME types
 * @param {boolean} props.multiple - Allow multiple file selection
 * @param {number} props.maxSize - Maximum file size in bytes
 * @param {string} props.title - Upload zone title
 * @param {string} props.description - Upload zone description
 * @param {React.Ref} props.inputRef - Optional ref to access the file input
 */
const FileUploadZone = ({
    onFilesSelected,
    acceptedTypes = ['application/pdf'],
    multiple = false,
    maxSize = 100 * 1024 * 1024, // 100MB default
    title = 'Drop your PDF files here',
    description = 'or click to browse',
    className = '',
    inputRef
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const fileInputRef = useRef(null);

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        handleFiles(files);
    };

    const handleFileInput = (e) => {
        const files = Array.from(e.target.files);
        handleFiles(files);
    };

    const handleFiles = (files) => {
        // Filter by accepted types
        const validFiles = files.filter(file => {
            if (acceptedTypes.length > 0 && !acceptedTypes.includes(file.type)) {
                return false;
            }
            if (file.size > maxSize) {
                return false;
            }
            return true;
        });

        if (!multiple && validFiles.length > 0) {
            setSelectedFiles([validFiles[0]]);
            onFilesSelected([validFiles[0]]);
        } else {
            setSelectedFiles(prev => [...prev, ...validFiles]);
            onFilesSelected(validFiles);
        }
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className={className}>
            <div
                className={`upload-zone ${isDragging ? 'drag-over' : ''}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                <Upload className="upload-zone-icon" />
                <h3 style={{
                    fontSize: 'var(--text-xl)',
                    fontWeight: 'var(--font-semibold)',
                    marginBottom: 'var(--spacing-2)',
                    color: 'var(--color-text-primary)'
                }}>
                    {title}
                </h3>
                <p style={{
                    fontSize: 'var(--text-base)',
                    color: 'var(--color-text-secondary)'
                }}>
                    {description}
                </p>
                <input
                    ref={(node) => {
                        fileInputRef.current = node;
                        if (inputRef) {
                            if (typeof inputRef === 'function') {
                                inputRef(node);
                            } else {
                                inputRef.current = node;
                            }
                        }
                    }}
                    type="file"
                    accept={acceptedTypes.join(',')}
                    multiple={multiple}
                    onChange={handleFileInput}
                    style={{ display: 'none' }}
                />
            </div>

            {selectedFiles.length > 0 && (
                <div style={{ marginTop: 'var(--spacing-6)' }}>
                    <h4 style={{
                        fontSize: 'var(--text-lg)',
                        fontWeight: 'var(--font-semibold)',
                        marginBottom: 'var(--spacing-3)',
                        color: 'var(--color-text-primary)'
                    }}>
                        Selected Files ({selectedFiles.length})
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
                        {selectedFiles.map((file, index) => (
                            <div
                                key={index}
                                className="card card-compact"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 'var(--spacing-3)'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
                                    <File size={20} color="var(--color-accent)" />
                                    <div>
                                        <div style={{
                                            fontSize: 'var(--text-sm)',
                                            fontWeight: 'var(--font-medium)',
                                            color: 'var(--color-text-primary)'
                                        }}>
                                            {file.name}
                                        </div>
                                        <div style={{
                                            fontSize: 'var(--text-xs)',
                                            color: 'var(--color-text-tertiary)'
                                        }}>
                                            {(file.size / 1024 / 1024).toFixed(2)} MB
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeFile(index);
                                    }}
                                    className="btn btn-ghost btn-sm btn-icon"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FileUploadZone;
